// Vue
import { computed, type ComputedRef, type WritableComputedRef } from "vue";

// Providers
import { useProjectWrapper } from "@/dataProviders/ProjectWrapper";
import useCreateBuildingProject from "../providers/CreateBuildingProjectProvider";

// Types
import type { WizardProjectType } from "../types/WizardProjectType";

/**
 * One write path for a wizard step screen.
 *
 * The step screens are shared by the create and edit wizards and are handed the store's live project as a
 * prop. Each of them used to do `const project = ref(props.project)` and mutate through it. That ref makes
 * nothing reactive which was not already, since the prop IS the store's reactive object; what it does is hide
 * the write from `vue/no-mutating-props`, and go stale if the prop is ever replaced.
 *
 * So this exists to give a step three things and take one away:
 *
 *  - `field` and `lens`, so a write is a setter in one place instead of a template mutating an object graph.
 *  - `isExistingProject` and `projectId`, so steps stop telling create from edit by sniffing for an `id` key.
 *    That check appeared in twelve places, each spelling out the same thing slightly differently.
 *  - `resetEmissionFactors`, which has to reach two stores and therefore belongs to one owner rather than
 *    being written out inside a step. Doing it by hand is what left the create flow throwing on every
 *    commissioning-year change, silently, because the edit store's project is undefined there.
 *
 * What it deliberately does not change: the project object stays shared with the store. The API serialises one
 * object and the store owns it, so writes land there by design. The narrower promise is that no component
 * keeps its own copy.
 *
 * Takes a getter rather than the value, so it follows a prop being replaced instead of capturing it once.
 */
export function useWizardProject(getProject: () => WizardProjectType) {
  const projectWrapper = useProjectWrapper();
  const createBuildingProject = useCreateBuildingProject();

  const project = computed(getProject);

  /** Whether this is a saved project. The presence of an id is still the test, but only here. */
  const isExistingProject: ComputedRef<boolean> = computed(() => Boolean(project.value.id));

  const projectId: ComputedRef<string | null> = computed(() => project.value.id ?? null);

  /**
   * A top-level field as a writable computed, for `v-model` on a step's inputs.
   *
   * No cast any more. It needed one while the parameter was the union of the two project types, because
   * TypeScript will not narrow an assignment through a union's index signature. Naming the shared shape
   * removed the union and the cast with it.
   */
  function field<K extends keyof WizardProjectType>(key: K): WritableComputedRef<WizardProjectType[K]> {
    return computed({
      get: () => project.value[key],
      set: (value) => {
        project.value[key] = value;
      },
    });
  }

  /** A nested field, named by the two functions that reach it. Used for anything under emissionCalculationAssumptions. */
  function lens<T>(
    get: (current: WizardProjectType) => T,
    set: (current: WizardProjectType, value: T) => void
  ): WritableComputedRef<T> {
    return computed({
      get: () => get(project.value),
      set: (value) => set(project.value, value),
    });
  }

  /** Whether any emission factor is set, so a caller can tell "nothing to lose" from "about to lose something". */
  const hasEmissionFactors: ComputedRef<boolean> = computed(() => {
    const energyData = project.value.emissionCalculationAssumptions.projectEnergyData;
    if (!energyData) return false;

    return [
      energyData.constructional?.heat,
      energyData.constructional?.electricity,
      energyData.constructional?.earthMoved,
      energyData.constructional?.machineryFuel,
      energyData.operational?.heat,
      energyData.operational?.electricity,
    ].some((row) => row && (row.sourceId || row.customSourceId || row.value !== null));
  });

  /**
   * Clear Installation's four consumption rows and Energy use's two.
   *
   * Both stores, because the shared step screens run under either wizard and only one of them holds the
   * project. Each store guards its own absent project, so calling both is correct rather than defensive.
   */
  function resetEmissionFactors() {
    projectWrapper.resetEmissionFactors();
    createBuildingProject.resetEmissionFactors();
  }

  return {
    project,
    isExistingProject,
    projectId,
    field,
    lens,
    hasEmissionFactors,
    resetEmissionFactors,
  };
}

export default useWizardProject;
